import * as THREE from 'three';
import { GeometryFactory } from './GeometryFactory.js';
import { MaterialFactory } from './MaterialFactory.js';
import { OBJECT_BY_ID } from '../data/objectCatalog.js';

const STRUCTURAL_TYPES = new Set(['wall', 'floor', 'roof', 'door', 'window', 'stair', 'ground', 'pool', 'architecture']);

export class ObjectFactory {
  constructor({ geometryFactory = new GeometryFactory(), materialFactory = new MaterialFactory() } = {}) {
    this.geometry = geometryFactory;
    this.materials = materialFactory;
  }

  createWallMesh(wall) {
    const [x1, z1] = wall.start;
    const [x2, z2] = wall.end;
    const dx = x2 - x1;
    const dz = z2 - z1;
    const length = Math.hypot(dx, dz);
    const mesh = new THREE.Mesh(
      this.geometry.box(Math.max(length, 0.05), wall.height, wall.thickness),
      this.materials.get(wall.material)
    );
    mesh.position.set((x1 + x2) / 2, wall.baseY + wall.height / 2, (z1 + z2) / 2);
    mesh.rotation.y = Math.atan2(-dz, dx);
    return this.#decorate(mesh, wall, 'wall');
  }

  createFloorMesh(room, floor) {
    const xs = room.polygon.map((point) => point[0]);
    const zs = room.polygon.map((point) => point[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minZ = Math.min(...zs);
    const maxZ = Math.max(...zs);
    const data = {
      ...room,
      type: 'floor',
      floorId: floor.id,
      material: room.floorMaterial,
      position: [(minX + maxX) / 2, floor.baseY ?? floor.level * floor.height, (minZ + maxZ) / 2]
    };
    const mesh = new THREE.Mesh(
      this.geometry.box(maxX - minX, floor.slabThickness, maxZ - minZ),
      this.materials.get(room.floorMaterial)
    );
    mesh.position.set(data.position[0], data.position[1] - floor.slabThickness / 2 + 0.02, data.position[2]);
    return this.#decorate(mesh, data, 'floor');
  }

  createRoofMesh(roof) {
    const group = new THREE.Group();
    const [width, , depth] = roof.dimensions;
    const eave = roof.eave ?? (roof.roofStyle === 'layered-hip' ? 1.15 : roof.roofStyle === 'hip' ? 0.9 : 0.62);
    const roofWidth = width + eave * 2;
    const roofDepth = depth + eave * 2;
    const roofHeight = roof.roofHeight ?? (roof.roofStyle === 'layered-hip' ? 1.9 : roof.roofStyle === 'hip' ? 1.55 : 1.4);
    if (roof.roofStyle === 'flat') {
      const parapet = roof.parapetMaterial ?? 'concrete-light';
      this.#box(group, [width + 0.45, 0.18, depth + 0.45], [0, 0.09, 0], roof.material);
      this.#box(group, [width + 0.62, 0.42, 0.18], [0, 0.29, -depth / 2 - 0.22], parapet);
      this.#box(group, [width + 0.62, 0.42, 0.18], [0, 0.29, depth / 2 + 0.22], parapet);
      this.#box(group, [0.18, 0.42, depth + 0.62], [-width / 2 - 0.22, 0.29, 0], parapet);
      this.#box(group, [0.18, 0.42, depth + 0.62], [width / 2 + 0.22, 0.29, 0], parapet);
    } else if (roof.roofStyle === 'layered-hip') {
      const lower = new THREE.Mesh(this.geometry.hipRoof(roofWidth, roofDepth, roofHeight), this.materials.get(roof.material));
      const upper = new THREE.Mesh(this.geometry.hipRoof(roofWidth * 0.54, roofDepth * 0.54, roofHeight * 0.62), this.materials.get(roof.material));
      upper.position.y = roofHeight * 0.62;
      group.add(lower, upper);
      this.#roofFascia(group, roofWidth, roofDepth, roof.material);
      this.#roofFascia(group, roofWidth * 0.54, roofDepth * 0.54, roof.material, roofHeight * 0.62);
    } else if (roof.roofStyle === 'split-gable') {
      this.#gablePanels(group, roofWidth * 0.49, roofDepth, -roofWidth * 0.255, roof.material, 0.42);
      this.#gablePanels(group, roofWidth * 0.49, roofDepth, roofWidth * 0.255, roof.material, 0.42);
      this.#box(group, [0.11, 0.11, roofDepth + 0.08], [-roofWidth * 0.255, 1.21, 0], roof.material);
      this.#box(group, [0.11, 0.11, roofDepth + 0.08], [roofWidth * 0.255, 1.21, 0], roof.material);
    } else if (roof.roofStyle === 'hip') {
      group.add(new THREE.Mesh(this.geometry.hipRoof(roofWidth, roofDepth, roofHeight), this.materials.get(roof.material)));
      this.#roofFascia(group, roofWidth, roofDepth, roof.material);
    } else if (roof.roofStyle === 'shed') {
      this.#box(group, [roofWidth, 0.2, roofDepth], [0, 0.55, 0], roof.material, [0, 0, -0.17]);
    } else {
      this.#gablePanels(group, roofWidth, roofDepth, 0, roof.material, 0.34);
    }
    this.#setTransform(group, roof);
    return this.#decorate(group, roof, 'roof');
  }

  createArchitecturalMesh(data) {
    const group = new THREE.Group();
    const [width = 1, height = 1, depth = 1] = data.dimensions ?? [1, 1, 1];
    const material = data.material ?? 'concrete-light';
    const accent = data.accentMaterial ?? material;

    if (data.generatorType === 'stilt-foundation') {
      for (const x of [-width * 0.38, 0, width * 0.38]) {
        for (const z of [-depth * 0.36, 0, depth * 0.36]) this.#box(group, [0.18, height, 0.18], [x, height / 2, z], material);
      }
      this.#box(group, [width, 0.2, depth], [0, height + 0.08, 0], material);
    } else if (data.generatorType === 'veranda') {
      this.#box(group, [width, 0.18, depth], [0, 0.09, 0], material);
      const postHeight = data.postHeight ?? 2.25;
      for (const x of [-width * 0.46, width * 0.46]) this.#box(group, [0.14, postHeight, 0.14], [x, postHeight / 2, depth * 0.28], accent);
      this.#box(group, [width + 0.35, 0.16, depth + 0.35], [0, postHeight, 0], data.roofMaterial ?? material, [-0.05, 0, 0]);
      this.#box(group, [width, 0.1, 0.1], [0, 0.82, depth * 0.47], accent);
    } else if (data.generatorType === 'entry-steps') {
      const steps = Math.max(3, Math.min(7, Math.ceil(height / 0.18)));
      const stepDepth = depth / steps;
      for (let index = 0; index < steps; index += 1) {
        const stepHeight = height * ((index + 1) / steps);
        this.#box(group, [width, stepHeight, stepDepth], [0, stepHeight / 2, -depth / 2 + stepDepth * (index + 0.5)], material);
      }
    } else if (data.generatorType === 'facade-frame') {
      this.#box(group, [0.2, height, depth], [-width / 2, height / 2, 0], material);
      this.#box(group, [0.2, height, depth], [width / 2, height / 2, 0], material);
      this.#box(group, [width + 0.2, 0.22, depth], [0, height - 0.11, 0], material);
      this.#box(group, [width * 0.34, 0.14, depth], [0, height * 0.24, 0], material);
    } else if (data.generatorType === 'carport') {
      this.#box(group, [width, 0.1, depth], [0, 0.05, 0], 'paving');
      for (const x of [-width * 0.44, width * 0.44]) {
        for (const z of [-depth * 0.42, depth * 0.42]) this.#box(group, [0.12, height, 0.12], [x, height / 2, z], accent);
      }
      this.#box(group, [width + 0.3, 0.18, depth + 0.3], [0, height, 0], material, [0, 0, data.rotationSlope ?? -0.035]);
    } else if (data.generatorType === 'parked-car') {
      const body = data.bodyMaterial ?? material;
      this.#box(group, [width, height * 0.48, depth], [0, height * 0.4, 0], body);
      this.#box(group, [width * 0.82, height * 0.43, depth * 0.48], [0, height * 0.78, -depth * 0.08], 'glass-smoke');
      this.#box(group, [width * 0.72, 0.06, 0.04], [0, height * 0.45, depth / 2 + 0.025], 'glass-clear');
      for (const x of [-width * 0.5, width * 0.5]) {
        for (const z of [-depth * 0.3, depth * 0.3]) {
          this.#cylinder(group, height * 0.18, height * 0.18, 0.16, [x, height * 0.22, z], 'metal-dark', [0, 0, Math.PI / 2]);
        }
      }
    } else if (data.generatorType === 'balcony') {
      this.#box(group, [width, height, depth], [0, height / 2, 0], material);
      const railHeight = 1.02;
      const railMaterial = data.railMaterial ?? 'metal-dark';
      for (let index = 0; index <= 6; index += 1) {
        const x = -width / 2 + (width / 6) * index;
        this.#box(group, [0.055, railHeight, 0.055], [x, height + railHeight / 2, depth / 2], railMaterial);
      }
      this.#box(group, [width + 0.08, 0.075, 0.075], [0, height + railHeight, depth / 2], railMaterial);
      for (const x of [-width / 2, width / 2]) this.#box(group, [0.055, railHeight, depth], [x, height + railHeight / 2, 0], railMaterial);
    } else if (data.generatorType === 'slat-screen') {
      const count = Math.max(4, Math.min(12, data.slatCount ?? 8));
      for (let index = 0; index < count; index += 1) {
        const x = -width / 2 + (width / Math.max(1, count - 1)) * index;
        this.#box(group, [0.075, height, depth], [x, height / 2, 0], material);
      }
    } else if (data.generatorType === 'facade-panel') {
      this.#box(group, [width, height, depth], [0, height / 2, 0], material);
      const strips = 3 + (data.panelPattern ?? 0);
      for (let index = 1; index < strips; index += 1) {
        this.#box(group, [width, 0.035, depth + 0.025], [0, (height / strips) * index, 0.015], 'metal-dark');
      }
    } else if (data.generatorType === 'planter') {
      this.#box(group, [width, height * 0.24, depth], [0, height * 0.12, 0], material);
      this.#box(group, [width, height, 0.09], [0, height / 2, -depth / 2], material);
      this.#box(group, [width, height, 0.09], [0, height / 2, depth / 2], material);
      const leafMaterial = this.materials.get('grass');
      for (let index = 0; index < 3; index += 1) {
        const leaf = new THREE.Mesh(this.geometry.sphere(0.22 + index * 0.035, 9, 6), leafMaterial);
        leaf.scale.set(0.7, 1.35, 0.7);
        leaf.position.set((index - 1) * width * 0.27, height + 0.22, 0);
        group.add(leaf);
      }
    } else if (data.generatorType === 'fence') {
      const gate = Math.min(width * 0.5, data.gateWidth ?? 3);
      const segment = (width - gate) / 2;
      this.#box(group, [segment, height * 0.38, depth], [-width / 2 + segment / 2, height * 0.19, 0], material);
      this.#box(group, [segment, height * 0.38, depth], [width / 2 - segment / 2, height * 0.19, 0], material);
      for (const x of [-width / 2, -gate / 2, gate / 2, width / 2]) this.#box(group, [0.14, height, depth + 0.08], [x, height / 2, 0], accent);
    } else if (data.generatorType === 'pergola') {
      for (const x of [-width * 0.45, width * 0.45]) {
        for (const z of [-depth * 0.42, depth * 0.42]) this.#box(group, [0.13, height, 0.13], [x, height / 2, z], material);
      }
      for (let index = 0; index < 6; index += 1) {
        const x = -width / 2 + (width / 5) * index;
        this.#box(group, [0.1, 0.12, depth + 0.3], [x, height, 0], material);
      }
    } else {
      this.#box(group, [width, height, depth], [0, height / 2, 0], material);
    }

    this.#setTransform(group, data);
    return this.#decorate(group, data, 'architecture');
  }

  createDoorMesh(door) {
    const group = new THREE.Group();
    const frame = this.#box(group, [door.width + 0.18, door.height + 0.12, 0.14], [0, door.height / 2, 0], door.frameMaterial ?? 'metal-dark');
    frame.userData.materialSlot = 'fixed';
    this.#box(group, [door.width - 0.08, door.height - 0.08, 0.17], [0, door.height / 2, 0.02], door.material);
    const threshold = this.#box(group, [door.width + 0.28, 0.07, 0.3], [0, 0.035, 0.045], 'stone-dark');
    threshold.userData.materialSlot = 'fixed';
    if (door.modern) {
      const insert = this.#box(group, [door.width * 0.24, door.height * 0.64, 0.025], [door.width * 0.22, door.height * 0.56, 0.115], 'glass-smoke');
      insert.userData.materialSlot = 'fixed';
    } else {
      for (const y of [door.height * 0.32, door.height * 0.65]) {
        const panel = this.#box(group, [door.width * 0.7, 0.045, 0.025], [0, y, 0.115], door.frameMaterial ?? 'wood-dark');
        panel.userData.materialSlot = 'fixed';
      }
    }
    const handle = new THREE.Mesh(this.geometry.sphere(0.055, 10, 8), this.materials.get('metal-dark'));
    handle.position.set(door.width * 0.32, door.height * 0.52, 0.12);
    handle.userData.materialSlot = 'fixed';
    group.add(handle);
    this.#setTransform(group, door);
    return this.#decorate(group, door, 'door');
  }

  createWindowMesh(windowData) {
    const group = new THREE.Group();
    const frameSize = Math.max(0.055, Math.min(0.09, Math.min(windowData.width, windowData.height) * 0.055));
    const fixedFrame = (dimensions, position) => {
      const mesh = this.#box(group, dimensions, position, windowData.frameMaterial ?? 'metal-dark');
      mesh.userData.materialSlot = 'fixed';
      return mesh;
    };
    fixedFrame([windowData.width + frameSize * 1.5, frameSize, 0.13], [0, windowData.height / 2, 0]);
    fixedFrame([windowData.width + frameSize * 1.5, frameSize, 0.13], [0, -windowData.height / 2, 0]);
    fixedFrame([frameSize, windowData.height, 0.13], [-windowData.width / 2, 0, 0]);
    fixedFrame([frameSize, windowData.height, 0.13], [windowData.width / 2, 0, 0]);
    const paneCount = windowData.width > 1.75 ? 3 : 2;
    for (let index = 1; index < paneCount; index += 1) {
      const x = -windowData.width / 2 + (windowData.width / paneCount) * index;
      fixedFrame([frameSize * 0.72, windowData.height, 0.135], [x, 0, 0]);
    }
    this.#box(group, [windowData.width - frameSize * 2, windowData.height - frameSize * 2, 0.035], [0, 0, -0.035], windowData.material);
    fixedFrame([windowData.width + 0.2, 0.07, 0.24], [0, -windowData.height / 2 - 0.06, 0.025]);
    if (windowData.deepFrame) fixedFrame([windowData.width + 0.28, 0.09, 0.42], [0, windowData.height / 2 + 0.13, 0.08]);
    this.#setTransform(group, windowData);
    return this.#decorate(group, windowData, 'window');
  }

  createStairMesh(stair) {
    const group = new THREE.Group();
    const steps = stair.steps ?? 12;
    for (let index = 0; index < steps; index += 1) {
      const height = ((index + 1) / steps) * stair.rise;
      const depth = stair.run / steps;
      this.#box(group, [stair.width, height, depth], [0, height / 2, -stair.run / 2 + depth * (index + 0.5)], stair.material);
    }
    this.#setTransform(group, stair);
    return this.#decorate(group, stair, 'stair');
  }

  createFurnitureMesh(catalogItemOrId, objectData) {
    const item = typeof catalogItemOrId === 'string' ? OBJECT_BY_ID.get(catalogItemOrId) : catalogItemOrId;
    const type = item?.generatorType ?? objectData.generatorType ?? 'cabinet';
    const group = new THREE.Group();
    const material = objectData.material ?? item?.defaultMaterial ?? 'floor-oak';
    const dims = objectData.dimensions ?? item?.dimensions ?? [1, 1, 1];
    const [width, height, depth] = dims;

    if (type === 'sofa') {
      this.#box(group, [width, height * 0.42, depth], [0, height * 0.21, 0], material);
      this.#box(group, [width, height * 0.65, depth * 0.22], [0, height * 0.62, -depth * 0.39], material);
      this.#box(group, [width * 0.09, height * 0.54, depth], [-width * 0.46, height * 0.42, 0], material);
      this.#box(group, [width * 0.09, height * 0.54, depth], [width * 0.46, height * 0.42, 0], material);
    } else if (type === 'table' || type === 'desk') {
      this.#box(group, [width, 0.12, depth], [0, height - 0.06, 0], material);
      for (const x of [-width * 0.4, width * 0.4]) for (const z of [-depth * 0.35, depth * 0.35]) this.#box(group, [0.1, height, 0.1], [x, height / 2, z], 'metal-dark');
    } else if (type === 'chair' || type === 'barber-chair') {
      const seatHeight = height * 0.48;
      this.#box(group, [width, 0.14, depth], [0, seatHeight, 0], material);
      this.#box(group, [width, height * 0.5, 0.12], [0, seatHeight + height * 0.25, -depth * 0.43], material);
      const legMat = type === 'barber-chair' ? 'metal-dark' : material;
      for (const x of [-width * 0.36, width * 0.36]) for (const z of [-depth * 0.32, depth * 0.32]) this.#box(group, [0.09, seatHeight, 0.09], [x, seatHeight / 2, z], legMat);
      if (type === 'barber-chair') this.#cylinder(group, 0.25, 0.32, 0.08, [0, 0.05, 0], 'metal-dark');
    } else if (type === 'bed') {
      this.#box(group, [width, height * 0.42, depth], [0, height * 0.21, 0], 'floor-oak');
      this.#box(group, [width * 0.94, height * 0.26, depth * 0.92], [0, height * 0.47, 0.05], material);
      this.#box(group, [width, height, 0.14], [0, height / 2, -depth * 0.47], 'floor-oak');
      this.#box(group, [width * 0.38, 0.12, depth * 0.2], [-width * 0.23, height * 0.66, -depth * 0.3], 'wall-warm');
      this.#box(group, [width * 0.38, 0.12, depth * 0.2], [width * 0.23, height * 0.66, -depth * 0.3], 'wall-warm');
    } else if (type === 'washer') {
      this.#box(group, dims, [0, height / 2, 0], material);
      const door = new THREE.Mesh(this.geometry.cylinder(width * 0.29, width * 0.29, 0.06, 20), this.materials.get('glass-clear'));
      door.rotation.x = Math.PI / 2;
      door.position.set(0, height * 0.47, depth / 2 + 0.04);
      group.add(door);
      this.#box(group, [width * 0.72, 0.1, 0.03], [0, height * 0.84, depth / 2 + 0.03], 'metal-dark');
    } else if (type === 'shelf' || type === 'cabinet') {
      this.#box(group, [0.09, height, depth], [-width / 2, height / 2, 0], material);
      this.#box(group, [0.09, height, depth], [width / 2, height / 2, 0], material);
      for (let index = 0; index < 4; index += 1) this.#box(group, [width, 0.09, depth], [0, (height / 3) * index, 0], material);
    } else if (type === 'counter') {
      this.#box(group, [width, height, depth], [0, height / 2, 0], material);
      this.#box(group, [width + 0.14, 0.12, depth + 0.12], [0, height + 0.06, 0], 'floor-oak');
    } else if (type === 'plant') {
      this.#cylinder(group, width * 0.28, width * 0.36, height * 0.35, [0, height * 0.175, 0], 'wall-clay');
      const leafMaterial = this.materials.get('grass');
      for (let index = 0; index < 7; index += 1) {
        const leaf = new THREE.Mesh(this.geometry.sphere(width * 0.28, 10, 7), leafMaterial);
        leaf.scale.set(0.55, 1.3, 0.45);
        leaf.position.set(Math.sin(index) * width * 0.2, height * (0.5 + (index % 3) * 0.12), Math.cos(index) * width * 0.2);
        leaf.rotation.z = (index - 3) * 0.12;
        group.add(leaf);
      }
    } else if (type === 'lamp') {
      this.#cylinder(group, 0.04, 0.04, height * 0.5, [0, height * 0.74, 0], 'metal-dark');
      const shade = new THREE.Mesh(this.geometry.cone(width * 0.5, height * 0.35, 16), this.materials.get(material));
      shade.rotation.x = Math.PI;
      shade.position.y = height * 0.38;
      group.add(shade);
    } else if (type === 'signage') {
      this.#box(group, dims, [0, height / 2, 0], material);
      this.#box(group, [width * 0.72, height * 0.09, depth + 0.03], [0, height * 0.62, depth * 0.55], 'wall-warm');
      this.#box(group, [width * 0.5, height * 0.06, depth + 0.03], [0, height * 0.38, depth * 0.55], 'wall-warm');
    } else {
      this.#box(group, dims, [0, height / 2, 0], material);
    }

    this.#setTransform(group, objectData);
    return this.#decorate(group, objectData, objectData.type ?? 'furniture');
  }

  createLandscapeMesh(data) {
    const group = new THREE.Group();
    if (data.generatorType === 'ground' || data.type === 'ground') {
      const [width, height, depth] = data.dimensions;
      this.#box(group, [width + 0.18, 0.12, depth + 0.18], [0, -height - 0.06, 0], 'stone-dark');
      this.#box(group, data.dimensions, [0, -height / 2, 0], data.material ?? 'grass');
    } else if (data.generatorType === 'pool' || data.type === 'pool') {
      this.#box(group, data.dimensions, [0, data.dimensions[1] / 2, 0], 'water');
      const [width, height, depth] = data.dimensions;
      this.#box(group, [width + 0.4, 0.12, 0.18], [0, height, -depth / 2], 'paving');
      this.#box(group, [width + 0.4, 0.12, 0.18], [0, height, depth / 2], 'paving');
      this.#box(group, [0.18, 0.12, depth], [-width / 2, height, 0], 'paving');
      this.#box(group, [0.18, 0.12, depth], [width / 2, height, 0], 'paving');
    } else if (data.generatorType === 'tree') {
      const [width = 1.1, height = 3.2] = data.dimensions ?? [];
      const trunkHeight = height * 0.56;
      this.#cylinder(group, width * 0.11, width * 0.16, trunkHeight, [0, trunkHeight / 2, 0], 'wood-dark');
      const leaves = this.materials.get('grass');
      for (const [x, y, z, scale] of [[0, height * 0.72, 0, 1], [-0.3, height * 0.64, 0.08, 0.75], [0.28, height * 0.68, -0.08, 0.8], [0.02, height * 0.86, 0.02, 0.72]]) {
        const crown = new THREE.Mesh(this.geometry.sphere(width * 0.52 * scale, 9, 6), leaves);
        crown.scale.set(1, 0.78, 1);
        crown.position.set(x * width, y, z * width);
        group.add(crown);
      }
    } else if (data.generatorType === 'shrub') {
      const [width = 0.85, height = 0.9] = data.dimensions ?? [];
      const leaves = this.materials.get('grass');
      for (const [x, scale] of [[-0.28, 0.78], [0, 1], [0.28, 0.76]]) {
        const crown = new THREE.Mesh(this.geometry.sphere(width * 0.45 * scale, 9, 6), leaves);
        crown.scale.set(1, 0.82, 1);
        crown.position.set(x * width, height * (0.45 + scale * 0.12), 0);
        group.add(crown);
      }
    } else {
      return this.createFurnitureMesh('plant-pot', data);
    }
    this.#setTransform(group, data);
    return this.#decorate(group, data, data.type ?? 'landscape');
  }

  applyMaterial(object, materialId, color) {
    const material = this.materials.get(materialId, color ? { color } : {});
    object.traverse((child) => {
      if (child.isMesh && child.userData.materialSlot !== 'fixed') child.material = material;
    });
    object.userData.materialId = materialId;
    return material;
  }

  #box(group, dimensions, position, material, rotation = [0, 0, 0]) {
    const mesh = new THREE.Mesh(this.geometry.box(...dimensions), this.materials.get(material));
    mesh.position.set(...position);
    mesh.rotation.set(...rotation);
    group.add(mesh);
    return mesh;
  }

  #cylinder(group, radiusTop, radiusBottom, height, position, material, rotation = [0, 0, 0]) {
    const mesh = new THREE.Mesh(this.geometry.cylinder(radiusTop, radiusBottom, height), this.materials.get(material));
    mesh.position.set(...position);
    mesh.rotation.set(...rotation);
    group.add(mesh);
    return mesh;
  }

  #roofFascia(group, width, depth, material, y = 0.03) {
    this.#box(group, [width + 0.08, 0.13, 0.11], [0, y, -depth / 2], material);
    this.#box(group, [width + 0.08, 0.13, 0.11], [0, y, depth / 2], material);
    this.#box(group, [0.11, 0.13, depth], [-width / 2, y, 0], material);
    this.#box(group, [0.11, 0.13, depth], [width / 2, y, 0], material);
  }

  #gablePanels(group, width, depth, centerX, material, slope) {
    const panelWidth = width / 2 + 0.45;
    this.#box(group, [panelWidth, 0.2, depth], [centerX - width / 4, 0.62, 0], material, [0, 0, -slope]);
    this.#box(group, [panelWidth, 0.2, depth], [centerX + width / 4, 0.62, 0], material, [0, 0, slope]);
  }

  #setTransform(object, data) {
    const [x = 0, y = 0, z = 0] = data.position ?? [0, 0, 0];
    const [rx = 0, ry = 0, rz = 0] = data.rotation ?? [0, 0, 0];
    const [sx = 1, sy = 1, sz = 1] = data.scale ?? [1, 1, 1];
    object.position.set(x, y, z);
    object.rotation.set(rx, ry, rz);
    object.scale.set(Math.max(0.05, sx), Math.max(0.05, sy), Math.max(0.05, sz));
  }

  #decorate(root, data, fallbackType) {
    root.name = data.name ?? data.id ?? fallbackType;
    const shared = {
      objectId: data.id,
      type: data.type ?? fallbackType,
      category: data.category ?? fallbackType,
      floorId: data.floorId ?? null,
      materialId: data.material ?? data.floorMaterial ?? null,
      materialSlot: data.materialSlot ?? 'editable',
      structural: STRUCTURAL_TYPES.has(data.type ?? fallbackType)
    };
    root.userData = { ...root.userData, ...shared };
    root.traverse((child) => {
      child.userData = { ...shared, ...child.userData };
      if (child.isMesh) {
        child.castShadow = !['glass-clear', 'water'].includes(shared.materialId);
        child.receiveShadow = true;
      }
    });
    return root;
  }

  dispose() {
    this.geometry.dispose();
    this.materials.dispose();
  }
}
